import { pageLanguage } from '../../lib/page-language'
import { Helmet } from 'react-helmet-async'
import { siteConfig } from '../../config/site'
import type { SeoContext } from '../../domain/seo'
import { buildJsonLd, buildSeoMeta } from '../../lib/seo'

export const SeoHead = (context: SeoContext) => {
  const meta = buildSeoMeta(context)
  const jsonLdDocument = buildJsonLd(context)
  const linkPreviewTitle = meta.shareTitle ?? meta.title
  const { image, imageAlt, imageWidth, imageHeight } = meta

  return (
    <Helmet>
      <html lang={context.language ?? pageLanguage(context.pathname)} />
      <link rel="icon" type="image/svg+xml" href={siteConfig.faviconPath} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      {siteConfig.googleSiteVerification ? (
        <meta name="google-site-verification" content={siteConfig.googleSiteVerification} />
      ) : null}
      {context.kind !== 'not-found' && <link rel="canonical" href={meta.canonical} />}
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:type" content={meta.ogType} />
      <meta property="og:title" content={linkPreviewTitle} />
      <meta property="og:description" content={meta.description} />
      {context.kind !== 'not-found' && <meta property="og:url" content={meta.canonical} />}
      {meta.articlePublishedTime ? (
        <meta property="article:published_time" content={meta.articlePublishedTime} />
      ) : null}
      {meta.articleModifiedTime ? (
        <meta property="article:modified_time" content={meta.articleModifiedTime} />
      ) : null}
      <meta property="og:image" content={image} />
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}
      {typeof imageWidth === 'number' ? (
        <meta property="og:image:width" content={String(imageWidth)} />
      ) : null}
      {typeof imageHeight === 'number' ? (
        <meta property="og:image:height" content={String(imageHeight)} />
      ) : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={linkPreviewTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />
      {imageAlt ? <meta name="twitter:image:alt" content={imageAlt} /> : null}
      {context.lcpImageUrl ? (
        <link
          rel="preload"
          as="image"
          href={context.lcpImageUrl}
          type="image/avif"
          {...(context.lcpImageSrcSet
            ? {
                imageSrcSet: context.lcpImageSrcSet,
                imageSizes: context.lcpImageSizes ?? '40px',
              }
            : {})}
        />
      ) : null}
      {jsonLdDocument ? (
        <script type="application/ld+json">{JSON.stringify(jsonLdDocument).replace(/</g, '\\u003c')}</script>
      ) : null}
    </Helmet>
  )
}
